const ATTRIBUTION_KEY = "zild.analytics.attribution";
const JOURNEY_KEY = "zild.analytics.journey";
const ANALYTICS_SCHEMA_VERSION = "2026-05-09";
const SCROLL_MILESTONES = [25, 50, 75, 90];

const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const CLICK_IDS = ["gclid", "fbclid", "msclkid", "gbraid", "wbraid"];
const ROUTE_LOCALES = new Set(["pt-BR", "en-US", "pt", "en"]);

let activePage = null;
let sessionDropoffSent = false;
const activeForms = new Map();

const cleanParams = (params = {}) =>
    Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));

const getStorage = () => {
    try {
        return window.sessionStorage;
    } catch (_) {
        return null;
    }
};

const readStoredRecord = (key) => {
    const storage = getStorage();
    if (!storage) return {};
    try {
        return JSON.parse(storage.getItem(key) || "{}");
    } catch (_) {
        return {};
    }
};

const writeStoredRecord = (key, value) => {
    const storage = getStorage();
    if (!storage) return;
    try {
        storage.setItem(key, JSON.stringify(cleanParams(value)));
    } catch (_) {}
};

const createSessionId = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const nowIso = () => new Date().toISOString();

const getScrollPercent = () => {
    if (typeof window === "undefined") return 0;
    const documentElement = document.documentElement;
    const scrollable = Math.max(documentElement.scrollHeight, document.body.scrollHeight) - window.innerHeight;
    if (scrollable <= 0) return 100;
    return Math.max(0, Math.min(100, Math.round((window.scrollY / scrollable) * 100)));
};

const getValueLengthBucket = (value = "") => {
    const length = String(value || "").trim().length;
    if (!length) return "empty";
    if (length < 20) return "short";
    if (length < 80) return "medium";
    return "long";
};

const getFieldValueState = (element) => {
    if (!element) return { hasValue: false, valueLengthBucket: "empty" };
    if (element.type === "checkbox" || element.type === "radio") {
        return { hasValue: Boolean(element.checked), valueLengthBucket: element.checked ? "selected" : "empty" };
    }
    return {
        hasValue: String(element.value || "").trim().length > 0,
        valueLengthBucket: getValueLengthBucket(element.value),
    };
};

const getDomain = (url) => {
    if (!url) return undefined;
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch (_) {
        return undefined;
    }
};

const isSearchDomain = (domain = "") =>
    ["google.", "bing.", "yahoo.", "duckduckgo.", "ecosia.", "baidu.", "yandex."].some((item) =>
        domain.includes(item),
    );

const isSocialDomain = (domain = "") =>
    ["linkedin.", "lnkd.", "facebook.", "instagram.", "t.co", "twitter.", "x.com", "youtube.", "tiktok.", "reddit."].some(
        (item) => domain.includes(item),
    );

const resolveTrafficChannel = (source = "", medium = "") => {
    const sourceText = source.toLowerCase();
    const mediumText = medium.toLowerCase();

    if (/cpc|ppc|paidsearch|sem|paid-search/.test(mediumText)) return "paid_search";
    if (/paid-social|paidsocial|social_paid|paid_social/.test(mediumText)) return "paid_social";
    if (/display|banner|programmatic|retargeting|remarketing/.test(mediumText)) return "display";
    if (mediumText.includes("email")) return "email";
    if (mediumText.includes("whatsapp") || sourceText.includes("whatsapp")) return "messaging";
    if (mediumText.includes("social") || isSocialDomain(sourceText)) return mediumText.includes("paid") ? "paid_social" : "organic_social";
    if (isSearchDomain(sourceText)) return "organic_search";
    if (mediumText === "referral") return "referral";
    if (mediumText === "none" || sourceText === "direct") return "direct";
    return "other";
};

const resolveTrafficContext = (context) => {
    const source = context.utm_source?.trim().toLowerCase();
    const medium = context.utm_medium?.trim().toLowerCase();
    const campaign = context.utm_campaign?.trim();
    const referrerDomain = context.landing_referrer_domain || getDomain(context.landing_referrer);

    if (source || medium) {
        const trafficSource = source || "(not set)";
        const trafficMedium = medium || "(not set)";
        return {
            traffic_source: trafficSource,
            traffic_medium: trafficMedium,
            traffic_channel: resolveTrafficChannel(trafficSource, trafficMedium),
            traffic_campaign: campaign,
        };
    }

    if (context.gclid || context.gbraid || context.wbraid) {
        return { traffic_source: "google", traffic_medium: "cpc", traffic_channel: "paid_search" };
    }

    if (context.msclkid) {
        return { traffic_source: "microsoft", traffic_medium: "cpc", traffic_channel: "paid_search" };
    }

    if (context.fbclid) {
        return { traffic_source: "facebook", traffic_medium: "social", traffic_channel: "organic_social" };
    }

    if (referrerDomain) {
        return {
            traffic_source: referrerDomain,
            traffic_medium: "referral",
            traffic_channel: resolveTrafficChannel(referrerDomain, "referral"),
        };
    }

    return { traffic_source: "direct", traffic_medium: "none", traffic_channel: "direct" };
};

export const normalizeLocalizedRoute = (path) => {
    if (path === "/precos") return "/pricing";
    if (["/contato", "/contact"].includes(path)) return "/contact";
    if (["/caso-de-uso", "/casos-de-uso", "/use-case"].includes(path)) return "/use-cases";
    if (path === "/plataforma") return "/platform";
    if (path === "/governanca") return "/trust";
    if (path === "/empresa") return "/company";
    return path;
};

export const normalizePath = (path = "/") => {
    const segments = path.split("/").filter(Boolean);
    if (!segments.length) return { normalizedPath: "/" };

    const [first, ...rest] = segments;
    if (ROUTE_LOCALES.has(first)) {
        const localizedPath = rest.length ? `/${rest.join("/")}` : "/";
        return { normalizedPath: normalizeLocalizedRoute(localizedPath), routeLocale: first };
    }

    return { normalizedPath: normalizeLocalizedRoute(path) };
};

export const resolvePageMeta = (path = "/") => {
    const slug = path.split("/").filter(Boolean).at(-1);
    if (path === "/") return pageMeta("home", "marketing", "home", undefined, "awareness", "talk_to_specialist", "contact_form_submit");
    if (path.startsWith("/blog/")) return pageMeta("blog_post", "content", `blog_${slug}`, slug, "awareness", "content_assisted_lead", "content_to_contact");
    if (path === "/blog") return pageMeta("blog_index", "content", "blog", undefined, "awareness", "content_engagement", "blog_post_click");
    if (path.startsWith("/solutions/")) return pageMeta("solution_detail", "solutions", `solution_${slug}`, slug, "consideration", "workflow_fit", "talk_to_specialist");
    if (path === "/solutions") return pageMeta("solutions_index", "solutions", "solutions", undefined, "consideration", "solution_interest", "solution_select");
    if (path.startsWith("/products/")) return pageMeta("product_detail", "products", `product_${slug}`, slug, "consideration", "product_interest", "talk_to_specialist");
    if (path === "/products") return pageMeta("products_index", "products", "products", undefined, "consideration", "product_interest", "product_select");
    if (path.startsWith("/industries/")) return pageMeta("industry_detail", "industries", `industry_${slug}`, slug, "consideration", "industry_fit", "talk_to_specialist");
    if (path === "/industries") return pageMeta("industries_index", "industries", "industries", undefined, "consideration", "industry_fit", "custom_workflow_click");
    if (path === "/platform") return pageMeta("platform", "platform", "platform", undefined, "consideration", "platform_interest", "talk_to_specialist");
    if (path === "/trust") return pageMeta("trust", "platform", "trust", undefined, "validation", "trust_validation", "talk_to_specialist");
    if (path === "/implementation") return pageMeta("implementation", "platform", "implementation", undefined, "evaluation", "launch_planning", "diagnostic_or_contact");
    if (path === "/use-cases") return pageMeta("use_cases", "solutions", "use_cases", undefined, "consideration", "use_case_interest", "talk_to_specialist");
    if (path === "/pricing") return pageMeta("pricing", "pricing", "pricing", undefined, "evaluation", "pricing_intent", "begin_checkout");
    if (path === "/diagnostico") return pageMeta("diagnostic", "conversion", "diagnostic", undefined, "conversion", "diagnostic_access", "diagnostic_gate_completed");
    if (path === "/contact") return pageMeta("contact", "conversion", "contact", undefined, "conversion", "demo_request", "contact_form_submit");
    if (path.startsWith("/docs")) return pageMeta("docs", "docs", "docs", slug, "research", "technical_research", "support_or_signup");
    if (path === "/support") return pageMeta("support", "support", "support", undefined, "support", "support_contact", "support_contact_click");
    if (path === "/privacy" || path === "/terms") return pageMeta("legal", "legal", slug || "legal", slug, "validation", "legal_review", "navigation_click");
    if (path === "/login" || path === "/signup") return pageMeta("app_entry", "product", slug || "app_entry", slug, "activation", "app_access", "app_entry_click");
    if (path.startsWith("/ia-") || path === "/automacao-com-ia") return pageMeta("campaign", "campaign", slug || "campaign", slug, "acquisition", "campaign_lead", "diagnostic_or_contact");
    return pageMeta("marketing", "marketing", slug || path.slice(1), slug, "awareness", "talk_to_specialist", "contact_form_submit");
};

const pageMeta = (pageType, pageGroup, routeName, routeSlug, funnelStage, conversionGoal, primaryConversion) => ({
    page_type: pageType,
    page_group: pageGroup,
    route_name: routeName,
    route_slug: routeSlug,
    funnel_stage: funnelStage,
    conversion_goal: conversionGoal,
    primary_conversion: primaryConversion,
});

export const captureAttribution = () => {
    if (typeof window === "undefined") return {};

    const stored = readStoredRecord(ATTRIBUTION_KEY);
    const params = new URLSearchParams(window.location.search);
    const context = {
        session_id: stored.session_id || createSessionId(),
        session_start_at: stored.session_start_at || new Date().toISOString(),
        landing_page_url: stored.landing_page_url || `${window.location.origin}${window.location.pathname}${window.location.search || ""}`,
        landing_page_path: stored.landing_page_path || window.location.pathname,
        landing_page_query: stored.landing_page_query || window.location.search || undefined,
        landing_referrer: stored.landing_referrer || document.referrer || undefined,
    };

    [...UTM_PARAMS, ...CLICK_IDS].forEach((key) => {
        context[key] = stored[key] || params.get(key) || undefined;
    });

    context.landing_referrer_domain = stored.landing_referrer_domain || getDomain(context.landing_referrer);
    Object.assign(context, {
        traffic_source: stored.traffic_source,
        traffic_medium: stored.traffic_medium,
        traffic_channel: stored.traffic_channel,
        traffic_campaign: stored.traffic_campaign,
    });

    const resolved = resolveTrafficContext(context);
    const next = {
        ...context,
        traffic_source: context.traffic_source || resolved.traffic_source,
        traffic_medium: context.traffic_medium || resolved.traffic_medium,
        traffic_channel: context.traffic_channel || resolved.traffic_channel,
        traffic_campaign: context.traffic_campaign || resolved.traffic_campaign,
    };

    writeStoredRecord(ATTRIBUTION_KEY, next);
    return next;
};

const advanceJourney = (location = window.location) => {
    const stored = readStoredRecord(JOURNEY_KEY);
    const { normalizedPath } = normalizePath(location.pathname);
    const meta = resolvePageMeta(normalizedPath);
    const route = `${location.pathname}${location.search || ""}`;
    const routeHistory = Array.isArray(stored.route_history) ? stored.route_history : [];
    const nextIndex = Number(stored.route_sequence_index || 0) + 1;
    const next = cleanParams({
        entry_page_path: stored.entry_page_path || location.pathname,
        entry_page_query: stored.entry_page_query || location.search || undefined,
        entry_page_type: stored.entry_page_type || meta.page_type,
        entry_page_group: stored.entry_page_group || meta.page_group,
        current_page_path: location.pathname,
        current_page_query: location.search || undefined,
        current_page_type: meta.page_type,
        current_page_group: meta.page_group,
        route_sequence_index: nextIndex,
        route_history: [...routeHistory, route].slice(-12),
    });

    writeStoredRecord(JOURNEY_KEY, next);

    return cleanParams({
        route_sequence_index: nextIndex,
        route_event_type: stored.current_page_path ? "route_change" : "entry",
        route_from: stored.current_page_path,
        route_to: location.pathname,
        route_transition: stored.current_page_path ? `${stored.current_page_path} -> ${location.pathname}` : "entry",
        route_history: next.route_history.join(" > "),
        previous_page_path: stored.current_page_path,
        previous_page_type: stored.current_page_type,
        previous_page_group: stored.current_page_group,
        entry_page_path: next.entry_page_path,
        entry_page_query: next.entry_page_query,
        entry_page_type: next.entry_page_type,
        entry_page_group: next.entry_page_group,
    });
};

export const getPageContext = (location = window.location) => {
    if (typeof window === "undefined") return {};

    const { normalizedPath, routeLocale } = normalizePath(location.pathname);
    return cleanParams({
        ...captureAttribution(),
        ...resolvePageMeta(normalizedPath),
        analytics_schema_version: ANALYTICS_SCHEMA_VERSION,
        locale: document.documentElement.lang || routeLocale || "pt-BR",
        route_locale: routeLocale,
        normalized_page_path: normalizedPath,
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_query: window.location.search || undefined,
        page_hash: window.location.hash || undefined,
        page_title: document.title || undefined,
        referrer: document.referrer || undefined,
        referrer_domain: getDomain(document.referrer),
    });
};

const getActivePageMetrics = () => {
    if (!activePage) return {};
    const engagementTimeMs = Math.max(0, Date.now() - activePage.startedAtMs);
    const currentScrollPercent = getScrollPercent();
    activePage.maxScrollPercent = Math.max(activePage.maxScrollPercent || 0, currentScrollPercent);

    return cleanParams({
        page_entered_at: activePage.enteredAt,
        page_engagement_time_ms: engagementTimeMs,
        page_engagement_time_sec: Math.round(engagementTimeMs / 1000),
        max_scroll_percent: activePage.maxScrollPercent,
        scroll_depth_milestones: Array.from(activePage.scrollMilestones || []).sort((a, b) => a - b).join(","),
        sections_seen: Array.from(activePage.sectionsSeen || []).join(","),
        sections_seen_count: activePage.sectionsSeen?.size,
    });
};

const getJourneySnapshot = () =>
    cleanParams({
        ...readStoredRecord(ATTRIBUTION_KEY),
        ...readStoredRecord(JOURNEY_KEY),
        ...getActivePageMetrics(),
    });

const startActivePage = (payload) => {
    activePage = {
        pageInstanceId: payload.page_instance_id,
        pagePath: payload.page_path,
        normalizedPagePath: payload.normalized_page_path,
        pageTitle: payload.page_title,
        pageType: payload.page_type,
        pageGroup: payload.page_group,
        routeName: payload.route_name,
        funnelStage: payload.funnel_stage,
        conversionGoal: payload.conversion_goal,
        enteredAt: nowIso(),
        startedAtMs: Date.now(),
        maxScrollPercent: getScrollPercent(),
        scrollMilestones: new Set(),
        sectionsSeen: new Set(),
        exitReasons: new Set(),
    };
};

const sendEvent = (eventName, params = {}) => {
    if (typeof window === "undefined") return;

    const payload = cleanParams(params);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: eventName,
        event_name: eventName,
        zild_event: true,
        zild_event_name: eventName,
        zild_schema_version: ANALYTICS_SCHEMA_VERSION,
        ...payload,
    });

    if (!window.zildAnalyticsDisabled && typeof window.gtag === "function") {
        window.gtag("event", eventName, payload);
    }
};

const isContactDestination = (destination = "") =>
    destination.includes("#contact") ||
    destination.startsWith("tel:") ||
    destination.startsWith("mailto:") ||
    destination.includes("wa.me") ||
    destination.includes("whatsapp");

const contactChannelFromDestination = (destination = "") => {
    const value = destination.toLowerCase();
    if (value.startsWith("tel:")) return "phone";
    if (value.startsWith("mailto:")) return "email";
    if (value.includes("wa.me") || value.includes("whatsapp")) return "whatsapp";
    if (value.includes("/contact") || value.includes("/contato") || value.includes("#contact")) return "contact_form";
    return "contact";
};

const isDirectContactChannel = (channel) => ["phone", "whatsapp", "email"].includes(channel);

const getFormId = (formOrElement, fallback = "unknown_form") => {
    const form = formOrElement?.tagName?.toLowerCase?.() === "form" ? formOrElement : formOrElement?.closest?.("form");
    return form?.dataset?.analyticsFormId || form?.getAttribute?.("data-testid") || form?.id || fallback;
};

const getFieldId = (element) =>
    element?.dataset?.analyticsField ||
    element?.getAttribute?.("data-testid") ||
    element?.name ||
    element?.id ||
    element?.getAttribute?.("aria-label") ||
    "unknown_field";

const getFormState = (formId, params = {}) => {
    if (!activeForms.has(formId)) {
        activeForms.set(formId, {
            formId,
            formVariant: params.form_variant,
            source: params.source,
            startedAt: nowIso(),
            startedAtMs: Date.now(),
            pagePath: params.page_path || window.location?.pathname,
            pageType: params.page_type,
            pageGroup: params.page_group,
            fieldsTouched: new Set(),
            fieldsCompleted: new Set(),
            submitted: false,
            abandoned: false,
        });
    }

    return activeForms.get(formId);
};

const markFormStarted = (params = {}) => {
    if (!params.form_id) return;
    getFormState(params.form_id, params);
};

const markFormSubmitted = (params = {}) => {
    if (!params.form_id) return;
    const state = activeForms.get(params.form_id);
    if (state) {
        state.submitted = true;
        activeForms.delete(params.form_id);
    }
};

const flushFormAbandon = (reason, context = {}) => {
    activeForms.forEach((state) => {
        if (state.submitted || state.abandoned || state.fieldsTouched.size === 0) return;
        state.abandoned = true;
        sendEvent("form_abandon", {
            ...context,
            form_id: state.formId,
            form_variant: state.formVariant,
            source: state.source,
            abandon_reason: reason,
            form_started_at: state.startedAt,
            form_engagement_time_ms: Math.max(0, Date.now() - state.startedAtMs),
            form_fields_touched: Array.from(state.fieldsTouched).join(","),
            form_fields_completed: Array.from(state.fieldsCompleted).join(","),
            form_fields_touched_count: state.fieldsTouched.size,
            form_fields_completed_count: state.fieldsCompleted.size,
            lead_status: "form_abandoned",
            lead_type: "demo_request",
            conversion_type: "lead_intent",
            conversion_channel: "contact_form",
            conversion_surface: state.pagePath,
        });
        activeForms.delete(state.formId);
    });
};

const buildLeadPayload = (params, channel, method, status) =>
    cleanParams({
        ...params,
        event_category: "conversion",
        method,
        channel,
        lead_status: status,
        lead_source: params.lead_source || params.source || params.traffic_source,
        lead_type: params.lead_type || (status?.startsWith("support") ? "support" : "sales_contact"),
        conversion_type: params.conversion_type || (status === "lead_generated" || isDirectContactChannel(channel) ? "lead" : "lead_intent"),
        conversion_channel: params.conversion_channel || channel,
        conversion_surface: params.conversion_surface || params.location || params.nav_area || params.source,
        conversion_target: params.destination || params.conversion_target || params.form_id,
        is_primary_conversion: status === "lead_generated" || isDirectContactChannel(channel) ? true : undefined,
    });

const sendLeadConversion = (params, pushGenerateLead = true) => {
    const payload = cleanParams({ ...params, event_category: "conversion", is_primary_conversion: true });
    if (pushGenerateLead) sendEvent("generate_lead", payload);
    sendEvent("lead_conversion", payload);
    sendEvent("lead_journey_snapshot", {
        ...payload,
        snapshot_reason: "lead_conversion",
        ...getJourneySnapshot(),
    });
};

const mirrorRecommendedEvents = (eventName, params) => {
    if (eventName === "cta_click") {
        sendEvent("select_content", {
            ...params,
            content_type: "cta",
            content_id: params.label || params.destination,
            item_name: params.label || params.destination,
        });

        if (isContactDestination(params.destination)) {
            const channel = contactChannelFromDestination(params.destination);
            const leadPayload = buildLeadPayload(params, channel, channel === "contact_form" ? "cta" : channel, "contact_intent");
            sendEvent("lead_intent", leadPayload);
            sendEvent("working_lead", leadPayload);
            if (isDirectContactChannel(channel)) sendLeadConversion(leadPayload);
        }
    }

    if (eventName === "navigation_click") {
        sendEvent("select_content", {
            ...params,
            content_type: "navigation",
            content_id: params.label || params.destination,
            item_name: params.label || params.destination,
        });
    }

    if (eventName === "purchase_click") {
        sendEvent("select_item", {
            ...params,
            item_list_name: "pricing_tiers",
            items: [
                {
                    item_id: params.plan,
                    item_name: params.plan ? `Zild ${params.plan}` : "Zild plan",
                    item_category: "subscription",
                    quantity: 1,
                },
            ],
        });
    }

    if (eventName === "contact_form_started") {
        const leadPayload = buildLeadPayload(params, "contact_form", "form", "form_started");
        sendEvent("form_start", { ...leadPayload, form_name: params.form_id });
        sendEvent("lead_intent", leadPayload);
    }

    if (eventName === "form_submit_attempt") {
        sendEvent("lead_intent", buildLeadPayload(params, "contact_form", "form", "form_attempted"));
    }

    if (eventName === "contact_form_validation_error" || eventName === "form_submit_error") {
        sendEvent("lead_error", buildLeadPayload(params, "contact_form", "form", eventName));
    }

    if (eventName === "generate_lead") {
        const channel = params.channel || params.conversion_channel || "contact_form";
        const leadPayload = buildLeadPayload(params, channel, params.form_id || params.source || channel, "lead_generated");
        sendEvent("working_lead", leadPayload);
        sendLeadConversion(leadPayload, false);
    }

    if (eventName === "whatsapp_click" || eventName === "phone_click" || eventName === "email_click" || eventName === "support_contact_click") {
        const channel = params.channel || eventName.replace("_click", "");
        const status = eventName === "support_contact_click" ? "support_contact_intent" : "contact_intent";
        const leadPayload = buildLeadPayload(params, channel, params.method || channel, status);
        sendEvent("working_lead", leadPayload);
        if (eventName !== "support_contact_click") sendLeadConversion(leadPayload);
    }
};

export const trackEvent = (eventName, params = {}) => {
    const payload = cleanParams({ ...getPageContext(), ...params });
    if (eventName === "contact_form_started") markFormStarted(payload);
    if (eventName === "form_submit" || eventName === "generate_lead" || eventName === "demo_booked") markFormSubmitted(payload);
    sendEvent(eventName, payload);
    mirrorRecommendedEvents(eventName, payload);
};

export const trackPageView = (location = window.location) => {
    const journey = advanceJourney(location);
    const payload = cleanParams({
        ...getPageContext(location),
        ...journey,
        page_instance_id: journey.route_sequence_index,
        is_entry_page: journey.route_event_type === "entry",
    });

    startActivePage(payload);
    sendEvent("page_view", payload);
    sendEvent("route_step", payload);
    sendEvent("funnel_step", {
        ...payload,
        funnel_step: payload.route_sequence_index,
        funnel_step_name: payload.route_name,
        funnel_step_stage: payload.funnel_stage,
    });
};

export const trackPageExit = (reason = "unknown", params = {}) => {
    if (!activePage || activePage.exitReasons?.has(reason)) return;
    activePage.exitReasons.add(reason);

    const payload = cleanParams({
        ...getPageContext(),
        ...getJourneySnapshot(),
        page_instance_id: activePage.pageInstanceId,
        page_path: activePage.pagePath,
        normalized_page_path: activePage.normalizedPagePath,
        page_type: activePage.pageType,
        page_group: activePage.pageGroup,
        route_name: activePage.routeName,
        funnel_stage: activePage.funnelStage,
        conversion_goal: activePage.conversionGoal,
        exit_reason: reason,
        ...params,
    });

    flushFormAbandon(reason, payload);
    sendEvent("page_exit", payload);

    if (reason !== "route_change" && !sessionDropoffSent) {
        sessionDropoffSent = true;
        sendEvent("session_dropoff", {
            ...payload,
            dropoff_reason: reason,
            dropoff_page_path: activePage.pagePath,
            dropoff_page_type: activePage.pageType,
            dropoff_page_group: activePage.pageGroup,
            dropoff_funnel_stage: activePage.funnelStage,
        });
    }

    if (reason === "route_change") {
        activePage = null;
    }
};

export const trackScrollDepth = () => {
    if (!activePage) return;
    const percent = getScrollPercent();
    activePage.maxScrollPercent = Math.max(activePage.maxScrollPercent || 0, percent);

    SCROLL_MILESTONES.forEach((milestone) => {
        if (percent < milestone || activePage.scrollMilestones.has(milestone)) return;
        activePage.scrollMilestones.add(milestone);
        sendEvent("scroll_depth", {
            ...getPageContext(),
            ...getActivePageMetrics(),
            page_instance_id: activePage.pageInstanceId,
            scroll_depth_threshold: milestone,
            percent_scrolled: percent,
        });
    });
};

export const trackSectionView = (element) => {
    if (!activePage || !element) return;
    const sectionId = element.dataset.analyticsSection || element.getAttribute("data-testid") || element.id;
    if (!sectionId || activePage.sectionsSeen.has(sectionId)) return;
    activePage.sectionsSeen.add(sectionId);

    const heading = element.querySelector?.("h1,h2,h3")?.textContent?.trim().replace(/\s+/g, " ").slice(0, 120);
    sendEvent("section_view", {
        ...getPageContext(),
        ...getActivePageMetrics(),
        page_instance_id: activePage.pageInstanceId,
        section_id: sectionId,
        section_name: heading || sectionId,
        section_index: activePage.sectionsSeen.size,
    });
};

export const trackFormFieldInteraction = (element, interactionType = "focus") => {
    const form = element?.closest?.("form");
    if (!form || element.dataset.analyticsSkip === "true") return;

    const formId = getFormId(form);
    const fieldId = getFieldId(element);
    const state = getFormState(formId, {
        form_variant: form.dataset.analyticsVariant,
        source: form.dataset.analyticsSource,
        ...getPageContext(),
    });
    const { hasValue, valueLengthBucket } = getFieldValueState(element);
    state.fieldsTouched.add(fieldId);
    if (hasValue) state.fieldsCompleted.add(fieldId);

    const payload = cleanParams({
        ...getPageContext(),
        form_id: formId,
        form_variant: form.dataset.analyticsVariant,
        field_id: fieldId,
        field_type: element.type || element.tagName?.toLowerCase(),
        interaction_type: interactionType,
        has_value: hasValue,
        value_length_bucket: valueLengthBucket,
        form_fields_touched_count: state.fieldsTouched.size,
        form_fields_completed_count: state.fieldsCompleted.size,
    });

    if (interactionType === "focus" && !state[`${fieldId}:focus`]) {
        state[`${fieldId}:focus`] = true;
        sendEvent("form_field_focus", payload);
    }

    if ((interactionType === "blur" || interactionType === "change") && hasValue && !state[`${fieldId}:complete`]) {
        state[`${fieldId}:complete`] = true;
        sendEvent("form_field_complete", payload);
    }
};

const getElementLabel = (element) =>
    (element.dataset.analyticsLabel || element.getAttribute("aria-label") || element.innerText || element.textContent || "")
        .trim()
        .replace(/\s+/g, " ")
        .slice(0, 120);

const getElementLocation = (element) => {
    if (element.dataset.analyticsLocation) return element.dataset.analyticsLocation;
    const section = element.closest("[data-testid]");
    return section?.getAttribute("data-testid") || undefined;
};

const absoluteHref = (href) => {
    try {
        return new URL(href, window.location.origin).href;
    } catch (_) {
        return href;
    }
};

const destinationForAnalytics = (href) => {
    if (href.startsWith("tel:") || href.startsWith("mailto:")) return href;
    const absolute = absoluteHref(href);
    try {
        const url = new URL(absolute);
        if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`;
        return absolute;
    } catch (_) {
        return href;
    }
};

const classifyAnchorEvent = (element, destination) => {
    const testId = element.getAttribute("data-testid") || "";
    const className = element.className || "";
    const channel = contactChannelFromDestination(destination);

    if (channel === "whatsapp") return "whatsapp_click";
    if (channel === "phone") return "phone_click";
    if (channel === "email") return "email_click";
    if (testId.startsWith("language-switch-")) return "language_switch_click";
    if (testId.startsWith("plan-cta-")) return "purchase_click";
    if (className.includes("zild-btn") || testId.includes("cta") || destination.includes("/contact") || destination.includes("/contato")) {
        return "cta_click";
    }
    return "navigation_click";
};

export const trackAnchorClick = (element) => {
    const href = element.getAttribute("href");
    if (!href || element.dataset.analyticsSkip === "true") return;

    const destination = destinationForAnalytics(href);
    const eventName = element.dataset.analyticsEvent || classifyAnchorEvent(element, destination);
    const testId = element.getAttribute("data-testid") || undefined;
    const params = cleanParams({
        label: getElementLabel(element),
        destination,
        href: absoluteHref(href),
        location: getElementLocation(element),
        test_id: testId,
        channel: contactChannelFromDestination(destination),
        conversion_type: isContactDestination(destination) ? "lead" : undefined,
        conversion_channel: isContactDestination(destination) ? contactChannelFromDestination(destination) : undefined,
        conversion_surface: getElementLocation(element),
        conversion_target: destination,
        method: element.dataset.analyticsMethod,
        locale_from: element.dataset.analyticsLocaleFrom,
        locale_to: element.dataset.analyticsLocaleTo,
        plan: testId?.startsWith("plan-cta-") ? testId.replace("plan-cta-", "") : undefined,
        lead_type: isContactDestination(destination) ? "sales_contact" : undefined,
    });

    trackEvent(eventName, params);
};

export const trackElementClick = (element) => {
    if (!element || element.dataset.analyticsSkip === "true") return;
    if (element.matches?.("a[href]")) {
        trackAnchorClick(element);
        return;
    }

    const tagName = element.tagName?.toLowerCase();
    const type = element.getAttribute?.("type");
    if (tagName === "button" && type === "submit") return;

    const testId = element.getAttribute?.("data-testid") || undefined;
    const className = element.className || "";
    const eventName =
        element.dataset.analyticsEvent ||
        (className.includes("zild-btn") || testId?.includes("cta") ? "cta_click" : "button_click");

    trackEvent(eventName, {
        label: getElementLabel(element),
        location: getElementLocation(element),
        test_id: testId,
        element_type: tagName || element.getAttribute?.("role") || "element",
    });
};
